import { useState } from 'react';
import axios from 'axios';
import { Settings2, Send, AlertCircle, Loader2, Mail, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';
import LocaleLink from '../components/LocaleLink';
import { useLanguage } from '../contexts/LanguageContext';

const TELEGRAM_BOT_URL = process.env.REACT_APP_TELEGRAM_BOT_URL || 'https://t.me/Murphi707_bot';
const API = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_EMAIL = 'support@quidory.com';

const t = {
  ar: {
    h1: 'إدارة الاشتراك',
    sub: 'افتح بوابة سترايب لتعديل اشتراك Quidanalyzer أو الفواتير أو الإلغاء.',
    sessionLabel: 'معرّف جلسة الدفع (Stripe Session ID)',
    sessionHelp: 'تجده في رسالة تأكيد الاشتراك من سترايب أو في الرابط الذي وصلك بعد عملية الدفع (يبدأ بـ cs_...).',
    sessionPh: 'cs_test_a1b2c3...',
    openPortal: 'فتح بوابة سترايب',
    opening: 'جارٍ الفتح...',
    needHelp: 'لا تجد معرّف الجلسة؟',
    contact: 'راسلنا على',
    or: 'أو',
    openBot: 'افتح البوت',
    back: 'العودة إلى Quidanalyzer',
  },
  en: {
    h1: 'Manage subscription',
    sub: 'Open the Stripe portal to update your Quidanalyzer payment method, view invoices, or cancel.',
    sessionLabel: 'Stripe Checkout Session ID',
    sessionHelp: 'You can find it in your Stripe receipt email or in the URL we redirected you to after payment (starts with cs_...).',
    sessionPh: 'cs_test_a1b2c3...',
    openPortal: 'Open Stripe portal',
    opening: 'Opening...',
    needHelp: 'Can\u2019t find your session ID?',
    contact: 'Email us at',
    or: 'or',
    openBot: 'Open the bot',
    back: 'Back to Quidanalyzer',
  },
};

const QuidanalyzerAccountPage = () => {
  const { language, isRTL } = useLanguage();
  const c = t[language];

  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = sessionId.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/quidanalyzer/portal`, {
        session_id: trimmed,
        origin: window.location.origin,
        lang: language,
      });
      if (res?.data?.url) window.location.href = res.data.url;
      else setError(language === 'ar' ? 'استجابة غير متوقعة من الخادم.' : 'Unexpected response from the server.');
    } catch (err) {
      setError(err?.response?.data?.detail || (language === 'ar' ? 'تعذر فتح بوابة الاشتراك.' : 'Could not open the subscription portal.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        titleEn="Manage Subscription | Quidanalyzer"
        titleAr="إدارة الاشتراك | Quidanalyzer"
        descEn="Manage your Quidanalyzer subscription — update payment method, view invoices, or cancel via the Stripe billing portal."
        descAr="إدارة اشتراك Quidanalyzer — تحديث وسيلة الدفع، عرض الفواتير، أو الإلغاء عبر بوابة سترايب."
        path="/quidanalyzer/account"
      />

      <div className="pt-16 min-h-[calc(100vh-4rem)] bg-background">
        <section className="py-16 lg:py-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="inline-flex p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mb-5">
                <Settings2 className="h-5 w-5 text-emerald-400" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-foreground" data-testid="qa-account-h">
                {c.h1}
              </h1>
              <p className="mt-3 text-sm text-muted-foreground max-w-xl">{c.sub}</p>
            </div>

            <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-5">
              <div>
                <label htmlFor="session-id" className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                  {c.sessionLabel}
                </label>
                <input
                  id="session-id"
                  data-testid="qa-account-session-input"
                  type="text"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  placeholder={c.sessionPh}
                  dir="ltr"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground font-mono text-sm placeholder:text-muted-foreground/40 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-colors"
                />
                <p className="mt-2 text-xs text-muted-foreground/70 leading-relaxed">{c.sessionHelp}</p>
              </div>

              {error && (
                <div data-testid="qa-account-error" className="flex items-start gap-2 p-3 rounded-lg border border-red-500/30 bg-red-500/5 text-xs text-red-300">
                  <AlertCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                data-testid="qa-account-submit"
                disabled={loading || !sessionId.trim()}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-emerald-500 text-emerald-950 text-sm font-semibold hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:-translate-y-px"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {c.opening}
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4" />
                    {c.openPortal}
                  </>
                )}
              </button>
            </form>

            {/* Help block */}
            <div className="mt-8 p-5 rounded-xl border border-border bg-card/60">
              <p className="text-sm font-medium text-foreground mb-2">{c.needHelp}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {c.contact}{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline">
                  {SUPPORT_EMAIL}
                </a>
                <span className="mx-2 text-border/60">·</span>
                <Mail className="inline h-3 w-3 text-muted-foreground/70 me-1" />
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <a
                  href={TELEGRAM_BOT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border bg-background text-foreground text-xs font-medium hover:bg-muted/50 transition-colors"
                >
                  <Send className="h-3.5 w-3.5 text-emerald-400" />
                  {c.openBot}
                </a>
                <LocaleLink
                  to="/quidanalyzer"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="qa-account-back"
                >
                  {isRTL ? '←' : '\u2190'} {c.back}
                </LocaleLink>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default QuidanalyzerAccountPage;
